import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    BookOpen, Search, ChevronDown, ChevronRight, Lock, ExternalLink,
    ArrowUpRight, Layers, Zap, LayoutDashboard, Megaphone, BarChart2,
    Globe, Users, Settings, Cpu, ShoppingBag, Plug, FileText, Rocket,
    Mail, Calendar, Image, Star, Bot, Store, Radar, TrendingUp, Palette,
    MessageSquare, Share2, HelpCircle, UserPlus, Film, Server, KeyRound,
    Link2, CreditCard
} from 'lucide-react';
import { getKnowledgeBase, KnowledgeModule, KnowledgeWorkflow } from '../../services/api';
import GlassCard from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import './KnowledgePage.css';

// ─────────────────────────────────────────────────────────────────────────────
// Module groupings — user-facing categorisation
// ─────────────────────────────────────────────────────────────────────────────

interface ModuleGroup {
    label: string;
    icon: React.FC<any>;
    keys: string[];
}

const MODULE_GROUPS: ModuleGroup[] = [
    { label: 'Getting Started',       icon: LayoutDashboard, keys: ['dashboard', 'onboarding', 'profile', 'billing'] },
    { label: 'Content & Creation',    icon: MessageSquare,   keys: ['content', 'brand', 'images', 'scripts', 'video', 'multichannel', 'reviews'] },
    { label: 'Marketing & Publishing',icon: Megaphone,       keys: ['campaigns', 'edm', 'scheduling', 'wordpress', 'social_accounts', 'organic_growth', 'seo'] },
    { label: 'Lead Generation',       icon: UserPlus,        keys: ['landing_pages', 'lead_capture', 'quizzes'] },
    { label: 'Analytics',             icon: BarChart2,       keys: ['analytics', 'optimization'] },
    { label: 'Products & Site',       icon: ShoppingBag,     keys: ['products', 'websites', 'domains'] },
    { label: 'AI & Automation',       icon: Cpu,             keys: ['agent', 'coordinator', 'autonomy', 'marketplace'] },
    { label: 'CRM & Teams',           icon: Users,           keys: ['leads_crm', 'integrations', 'teams'] },
    { label: 'Account Settings',      icon: Settings,        keys: ['sending_email', 'sending_throttle', 'sms', 'ai_models', 'admin'] },
];

// Keys to omit from the knowledge base (legacy / sub-modules)
const HIDDEN_KEYS = new Set(['websites_v2', 'products_subscriptions', 'products_analytics', 'products_marketing_loop', 'support', 'admin']);

// ─────────────────────────────────────────────────────────────────────────────
// Related modules map — cross-references per module
// ─────────────────────────────────────────────────────────────────────────────

const RELATED: Record<string, string[]> = {
    content:        ['brand', 'scheduling', 'multichannel', 'images', 'seo'],
    brand:          ['content', 'landing_pages', 'edm', 'websites'],
    campaigns:      ['edm', 'landing_pages', 'analytics', 'lead_capture'],
    edm:            ['campaigns', 'lead_capture', 'sending_email'],
    landing_pages:  ['lead_capture', 'quizzes', 'brand', 'campaigns'],
    lead_capture:   ['landing_pages', 'leads_crm', 'campaigns', 'edm'],
    scheduling:     ['content', 'wordpress', 'social_accounts'],
    wordpress:      ['content', 'scheduling', 'domains'],
    analytics:      ['campaigns', 'optimization', 'leads_crm'],
    optimization:   ['analytics', 'landing_pages'],
    websites:       ['brand', 'lead_capture', 'domains'],
    domains:        ['wordpress', 'landing_pages', 'websites'],
    leads_crm:      ['lead_capture', 'campaigns', 'integrations'],
    integrations:   ['leads_crm', 'sending_email'],
    multichannel:   ['content', 'images', 'social_accounts'],
    images:         ['content', 'multichannel', 'brand'],
    seo:            ['content', 'wordpress'],
    quizzes:        ['landing_pages', 'lead_capture'],
    social_accounts:['scheduling', 'multichannel', 'campaigns'],
    agent:          ['coordinator', 'autonomy', 'content'],
    coordinator:    ['agent', 'autonomy'],
    autonomy:       ['agent', 'coordinator', 'campaigns', 'content'],
    marketplace:    ['websites', 'campaigns'],
    sending_email:  ['edm', 'lead_capture', 'sms'],
    sms:            ['edm', 'campaigns', 'lead_capture'],
    billing:        ['profile'],
    profile:        ['billing', 'sending_email', 'ai_models'],
};

// ─────────────────────────────────────────────────────────────────────────────
// Tier badge config
// ─────────────────────────────────────────────────────────────────────────────

const TIER_LABELS: Record<string, string> = {
    trial:      'Trial',
    growth:     'Growth',
    pro:        'Pro',
    enterprise: 'Enterprise',
    admin:      'Admin',
};

const TIER_COLOURS: Record<string, string> = {
    trial:      'var(--text-muted)',
    growth:     'var(--primary-500)',
    pro:        '#9333ea',
    enterprise: '#d97706',
    admin:      'var(--danger)',
};

// ─────────────────────────────────────────────────────────────────────────────
// Strip [NAVIGATE:...] markup from how-to text (assistant-internal links)
// ─────────────────────────────────────────────────────────────────────────────

function stripNavigate(text: string): string {
    return text.replace(/\[NAVIGATE:[^\]]+\]/g, '').trim();
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

type Selection =
    | { type: 'module'; key: string }
    | { type: 'workflow'; key: string }
    | null;

const KnowledgePage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [modules, setModules] = useState<KnowledgeModule[]>([]);
    const [workflows, setWorkflows] = useState<KnowledgeWorkflow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set(MODULE_GROUPS.map(g => g.label)));
    const [workflowsExpanded, setWorkflowsExpanded] = useState(true);

    // Selected item from URL param: ?module=content or ?workflow=publish_article
    const selectionFromUrl = useMemo<Selection>(() => {
        const mod = searchParams.get('module');
        const wf = searchParams.get('workflow');
        if (mod) return { type: 'module', key: mod };
        if (wf) return { type: 'workflow', key: wf };
        return null;
    }, [searchParams]);

    const [selection, setSelection] = useState<Selection>(selectionFromUrl);

    useEffect(() => {
        setSelection(selectionFromUrl);
    }, [selectionFromUrl]);

    const select = useCallback((s: Selection) => {
        setSelection(s);
        if (s?.type === 'module') {
            setSearchParams({ module: s.key });
        } else if (s?.type === 'workflow') {
            setSearchParams({ workflow: s.key });
        } else {
            setSearchParams({});
        }
    }, [setSearchParams]);

    useEffect(() => {
        getKnowledgeBase()
            .then(data => {
                setModules(data.modules.filter(m => !HIDDEN_KEYS.has(m.key)));
                setWorkflows(data.workflows);
                // Default to dashboard if no selection
                if (!selectionFromUrl) {
                    const dash = data.modules.find(m => m.key === 'dashboard');
                    if (dash) select({ type: 'module', key: 'dashboard' });
                }
            })
            .catch(() => setError('Failed to load knowledge base.'))
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Module lookup map
    const moduleMap = useMemo(() => {
        const map: Record<string, KnowledgeModule> = {};
        modules.forEach(m => { map[m.key] = m; });
        return map;
    }, [modules]);

    // Workflow lookup map
    const workflowMap = useMemo(() => {
        const map: Record<string, KnowledgeWorkflow> = {};
        workflows.forEach(w => { map[w.key] = w; });
        return map;
    }, [workflows]);

    // Filter nav items by search
    const query = search.toLowerCase().trim();
    const matchesModule = (m: KnowledgeModule) =>
        !query ||
        m.name.toLowerCase().includes(query) ||
        m.description.toLowerCase().includes(query) ||
        m.key_features.some(f => f.toLowerCase().includes(query));
    const matchesWorkflow = (w: KnowledgeWorkflow) =>
        !query || w.name.toLowerCase().includes(query);

    const toggleGroup = (label: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(label)) next.delete(label); else next.add(label);
            return next;
        });
    };

    // ── Render selected content ────────────────────────────────────────────
    const renderContent = () => {
        if (!selection) {
            return (
                <div className="kb-empty">
                    <BookOpen size={48} style={{ color: 'var(--text-muted)', marginBottom: 16 }} />
                    <p>Select a module or guide from the left to get started.</p>
                </div>
            );
        }

        if (selection.type === 'workflow') {
            const wf = workflowMap[selection.key];
            if (!wf) return null;
            return <WorkflowArticle workflow={wf} moduleMap={moduleMap} onSelectModule={(key) => select({ type: 'module', key })} />;
        }

        const mod = moduleMap[selection.key];
        if (!mod) return null;
        return (
            <ModuleArticle
                module={mod}
                relatedKeys={RELATED[mod.key] ?? []}
                moduleMap={moduleMap}
                onSelectModule={(key) => select({ type: 'module', key })}
            />
        );
    };

    if (loading) {
        return (
            <div className="kb-page">
                <div className="kb-loading">Loading knowledge base…</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="kb-page">
                <div className="kb-error">{error}</div>
            </div>
        );
    }

    return (
        <div className="kb-page">
            {/* ── Left nav ──────────────────────────────────────────── */}
            <aside className="kb-nav">
                <div className="kb-nav-header">
                    <BookOpen size={18} />
                    <span>Knowledge Base</span>
                </div>

                <div className="kb-search-wrap">
                    <Search size={14} className="kb-search-icon" />
                    <input
                        className="kb-search"
                        type="text"
                        placeholder="Search modules…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                <nav className="kb-nav-list">
                    {/* Guides / Workflows section */}
                    {workflows.filter(matchesWorkflow).length > 0 && (
                        <div className="kb-nav-group">
                            <button
                                className="kb-nav-group-header"
                                onClick={() => setWorkflowsExpanded(v => !v)}
                            >
                                <Layers size={14} />
                                <span>Guides</span>
                                {workflowsExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                            </button>
                            {workflowsExpanded && workflows.filter(matchesWorkflow).map(wf => (
                                <button
                                    key={wf.key}
                                    className={`kb-nav-item ${selection?.type === 'workflow' && selection.key === wf.key ? 'active' : ''}`}
                                    onClick={() => select({ type: 'workflow', key: wf.key })}
                                >
                                    {wf.name}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Module groups */}
                    {MODULE_GROUPS.map(group => {
                        const GroupIcon = group.icon;
                        const groupModules = group.keys
                            .map(k => moduleMap[k])
                            .filter((m): m is KnowledgeModule => !!m && matchesModule(m));
                        if (groupModules.length === 0) return null;
                        const expanded = expandedGroups.has(group.label);
                        return (
                            <div key={group.label} className="kb-nav-group">
                                <button
                                    className="kb-nav-group-header"
                                    onClick={() => toggleGroup(group.label)}
                                >
                                    <GroupIcon size={14} />
                                    <span>{group.label}</span>
                                    {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                                </button>
                                {expanded && groupModules.map(m => (
                                    <button
                                        key={m.key}
                                        className={`kb-nav-item ${selection?.type === 'module' && selection.key === m.key ? 'active' : ''} ${!m.accessible ? 'locked' : ''}`}
                                        onClick={() => select({ type: 'module', key: m.key })}
                                    >
                                        <span>{m.name}</span>
                                        {!m.accessible && <Lock size={11} className="kb-lock-icon" />}
                                    </button>
                                ))}
                            </div>
                        );
                    })}
                </nav>
            </aside>

            {/* ── Content area ──────────────────────────────────────── */}
            <main className="kb-content">
                {renderContent()}
            </main>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Module article
// ─────────────────────────────────────────────────────────────────────────────

interface ModuleArticleProps {
    module: KnowledgeModule;
    relatedKeys: string[];
    moduleMap: Record<string, KnowledgeModule>;
    onSelectModule: (key: string) => void;
}

const FEATURES_PREVIEW_COUNT = 5;

const ModuleArticle: React.FC<ModuleArticleProps> = ({ module: mod, relatedKeys, moduleMap, onSelectModule }) => {
    const navigate = useNavigate();
    const [featuresExpanded, setFeaturesExpanded] = useState(false);
    useEffect(() => { setFeaturesExpanded(false); }, [mod.key]);
    const tierColour = TIER_COLOURS[mod.required_tier] ?? TIER_COLOURS.trial;
    const tierLabel = TIER_LABELS[mod.required_tier] ?? mod.required_tier;

    const relatedModules = relatedKeys
        .map(k => moduleMap[k])
        .filter((m): m is KnowledgeModule => !!m);

    return (
        <div className="kb-article">
            {/* Header */}
            <div className="kb-article-header">
                <div className="kb-article-title-row">
                    <h1 className="kb-article-title">{mod.name}</h1>
                    <span
                        className="kb-tier-badge"
                        style={{ borderColor: tierColour, color: tierColour }}
                    >
                        {tierLabel}
                    </span>
                    {mod.route && (
                        <span className="kb-route-chip">{mod.route}</span>
                    )}
                </div>
            </div>

            {/* Upgrade gate */}
            {!mod.accessible && (
                <GlassCard variant="default" padding="md" className="kb-upgrade-card">
                    <div className="kb-upgrade-content">
                        <Lock size={20} style={{ color: tierColour, flexShrink: 0 }} />
                        <div>
                            <p className="kb-upgrade-title">
                                This module requires a <strong style={{ color: tierColour }}>{tierLabel}</strong> plan
                            </p>
                            <p className="kb-upgrade-sub">
                                Upgrade your subscription to unlock {mod.name} and all other {tierLabel} tier features.
                            </p>
                        </div>
                        <GlassButton
                            variant="primary"
                            size="sm"
                            pill
                            icon={<ArrowUpRight size={14} />}
                            iconPosition="right"
                            onClick={() => navigate('/billing')}
                        >
                            Upgrade plan
                        </GlassButton>
                    </div>
                </GlassCard>
            )}

            {/* Description */}
            <p className="kb-description">{mod.description}</p>

            {/* Key features */}
            {mod.key_features.length > 0 && (
                <section className="kb-section">
                    <h2 className="kb-section-title">Key Features</h2>
                    <ul className="kb-feature-list">
                        {(featuresExpanded ? mod.key_features : mod.key_features.slice(0, FEATURES_PREVIEW_COUNT)).map((f, i) => (
                            <li key={i}>{f}</li>
                        ))}
                    </ul>
                    {mod.key_features.length > FEATURES_PREVIEW_COUNT && (
                        <button
                            className="kb-features-toggle"
                            onClick={() => setFeaturesExpanded(v => !v)}
                        >
                            {featuresExpanded
                                ? <><ChevronDown size={13} style={{ transform: 'rotate(180deg)' }} /> Show fewer</>
                                : <><ChevronDown size={13} /> Show all {mod.key_features.length} features</>
                            }
                        </button>
                    )}
                </section>
            )}

            {/* How-to */}
            {mod.user_actions.length > 0 && (
                <section className="kb-section">
                    <h2 className="kb-section-title">How To</h2>
                    {mod.user_actions.some(ua => ua.tab)
                        ? (() => {
                            // Group by tab, preserving insertion order
                            const tabOrder: string[] = [];
                            const tabMap: Record<string, typeof mod.user_actions> = {};
                            mod.user_actions.forEach(ua => {
                                const t = ua.tab ?? 'General';
                                if (!tabMap[t]) { tabMap[t] = []; tabOrder.push(t); }
                                tabMap[t].push(ua);
                            });
                            return tabOrder.map(tab => (
                                <div key={tab} className="kb-howto-group">
                                    <h3 className="kb-howto-group-title">{tab}</h3>
                                    <ol className="kb-howto-list">
                                        {tabMap[tab].map((ua, i) => (
                                            <li key={i} className="kb-howto-item">
                                                <span className="kb-howto-action">{ua.action}</span>
                                                <span className="kb-howto-how">{stripNavigate(ua.how)}</span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            ));
                        })()
                        : (
                            <ol className="kb-howto-list">
                                {mod.user_actions.map((ua, i) => (
                                    <li key={i} className="kb-howto-item">
                                        <span className="kb-howto-action">{ua.action}</span>
                                        <span className="kb-howto-how">{stripNavigate(ua.how)}</span>
                                    </li>
                                ))}
                            </ol>
                        )
                    }
                </section>
            )}

            {/* Related modules */}
            {relatedModules.length > 0 && (
                <section className="kb-section">
                    <h2 className="kb-section-title">Related Modules</h2>
                    <div className="kb-related-pills">
                        {relatedModules.map(m => (
                            <button
                                key={m.key}
                                className={`kb-related-pill ${!m.accessible ? 'locked' : ''}`}
                                onClick={() => onSelectModule(m.key)}
                            >
                                {!m.accessible && <Lock size={11} />}
                                {m.name}
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {/* Open module link (if accessible) */}
            {mod.accessible && mod.route && mod.route !== '/scheduling' && (
                <div className="kb-open-link">
                    <GlassButton
                        variant="secondary"
                        size="sm"
                        icon={<ExternalLink size={13} />}
                        iconPosition="right"
                        onClick={() => navigate(mod.route)}
                    >
                        Open {mod.name}
                    </GlassButton>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Workflow article
// ─────────────────────────────────────────────────────────────────────────────

interface WorkflowArticleProps {
    workflow: KnowledgeWorkflow;
    moduleMap: Record<string, KnowledgeModule>;
    onSelectModule: (key: string) => void;
}

const WorkflowArticle: React.FC<WorkflowArticleProps> = ({ workflow: wf, moduleMap, onSelectModule }) => {
    return (
        <div className="kb-article">
            <div className="kb-article-header">
                <div className="kb-article-title-row">
                    <Layers size={22} style={{ color: 'var(--primary-500)', flexShrink: 0 }} />
                    <h1 className="kb-article-title">{wf.name}</h1>
                    <span className="kb-tier-badge" style={{ borderColor: 'var(--primary-500)', color: 'var(--primary-500)' }}>
                        Guide
                    </span>
                </div>
            </div>

            <section className="kb-section">
                <h2 className="kb-section-title">Steps</h2>
                <ol className="kb-workflow-steps">
                    {wf.steps.map((step, i) => (
                        <li key={i} className="kb-workflow-step">
                            {stripNavigate(step)}
                        </li>
                    ))}
                </ol>
            </section>
        </div>
    );
};

export default KnowledgePage;
